import type { HelpGuide } from '../types';

export const arGuide: HelpGuide = {
  key: 'ar',
  title: 'ลูกหนี้การค้า (Accounts Receivable)',
  subtitle: 'เมนู: ลูกหนี้ / เจ้าหนี้ ▸ ลูกหนี้การค้า',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/ar/คู่มือการใช้งาน_ลูกหนี้การค้า_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ลูกหนี้การค้า (Accounts Receivable)” ใช้คุมลูกหนี้การค้าทั้งวงจร ตั้งแต่ออกใบแจ้งหนี้ให้ลูกค้า · บันทึกการรับชำระเงิน (พร้อมหัก ณ ที่จ่าย) · ติดตามยอดคงค้างและใบที่เกินกำหนด ไปจนถึงเครื่องมือเสริม เช่น ใบลดหนี้ · ใบยกเลิก · ใบวางบิล · เช็ครับ (PDC) และเงินรับล่วงหน้า',
        },
        {
          kind: 'callout',
          text: 'หลักการบันทึกบัญชี: ออกใบแจ้งหนี้ → Dr ลูกหนี้การค้า / Cr รายได้ + ภาษีขาย (VAT) · รับชำระ → Dr เงินสด/ธนาคาร + Dr ภาษีหัก ณ ที่จ่าย (ถ้ามี) / Cr ลูกหนี้การค้า · ระบบลงใบสำคัญ (JV) ให้อัตโนมัติทุกครั้งที่สร้างใบแจ้งหนี้และรับชำระ',
        },
        { kind: 'para', text: 'หน้าจอแยกใบแจ้งหนี้ออกเป็น 5 แท็บ:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'ทั้งหมด — ', text: 'ใบแจ้งหนี้ทุกสถานะ (แท็บเริ่มต้น)' },
            { lead: 'รอชำระ — ', text: 'ใบที่ลงบัญชีแล้วและยังรอรับชำระ' },
            { lead: 'เกินกำหนด — ', text: 'ใบที่เลยวันครบกำหนดชำระแล้ว (ต้องเร่งติดตาม)' },
            { lead: 'ครบกำหนดเร็วๆนี้ — ', text: 'ใบที่จะครบกำหนดภายใน 7 วัน' },
            { lead: 'ชำระแล้ว — ', text: 'ใบที่รับชำระครบเต็มจำนวนแล้ว' },
          ],
        },
        {
          kind: 'image',
          src: '/help/ar/01-overview.png',
          caption:
            'ภาพรวม — KPI 4 ใบ (ใบแจ้งหนี้รวม/รับชำระแล้ว/คงค้าง/เกินกำหนด), แท็บ และตารางใบแจ้งหนี้พร้อมสถานะ',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ ด้านบน:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ใบแจ้งหนี้รวม — ',
              text: 'ยอดออกใบแจ้งหนี้รวม (THB) พร้อมจำนวนใบและฐานปีนี้ (YTD)',
            },
            { lead: 'รับชำระแล้ว — ', text: 'ยอดที่รับชำระแล้วทั้งหมด พร้อม % ของยอดรวม' },
            {
              lead: 'คงค้างชำระ — ',
              text: 'ยอดลูกหนี้คงค้างที่ยังไม่ได้รับชำระ พร้อมค่าเฉลี่ย DSO (วัน)',
            },
            { lead: 'เกินกำหนด — ', text: 'ยอดที่เลยกำหนดชำระ พร้อมจำนวนใบที่ต้องติดตาม' },
          ],
        },
        {
          kind: 'para',
          text: 'ปุ่มบนแถบเครื่องมือด้านบน: สร้างใบแจ้งหนี้ (ปุ่มหลักสีแดง) · รับชำระ · นำเข้า · ส่งออก · รายงานอายุหนี้ · DSO · ใบรับชำระ · ใบลดหนี้ · ใบยกเลิก · ใบวางบิล · เช็ครับ (PDC) · รับล่วงหน้า และปุ่มสลับมุมมองตาราง (กระชับ/โปร่ง)',
        },
        {
          kind: 'para',
          text: 'ตารางใบแจ้งหนี้กรองได้ด้วย ช่องค้นหา (เลขที่ใบแจ้งหนี้ / ชื่อลูกค้า) · สาขา/หน่วยงาน · เงื่อนไขชำระ · ช่วงวันที่ (จาก–ถึง) และยังบันทึกชุดตัวกรองเป็น “มุมมองที่บันทึก” ไว้เรียกใช้ซ้ำได้ คอลัมน์ในตาราง: เลขที่ใบแจ้งหนี้ · ลูกค้า · สาขา · วันที่ออก · กำหนดชำระ · มูลค่ารวม · รับชำระ · คงค้าง · อายุ (วัน) · สถานะ — คลิกที่เลขที่ใบเพื่อเปิดดูรายละเอียด',
        },
      ],
    },
    {
      id: 'overdue',
      heading: '3. การติดตามใบที่เกินกำหนด',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ เกินกำหนด แสดงเฉพาะใบแจ้งหนี้ที่ลงบัญชีแล้วและเลยวันครบกำหนดชำระ ใช้เร่งติดตามหนี้ คอลัมน์ กำหนดชำระ และ อายุ (วัน) จะขึ้นป้ายสีแดงพร้อมจำนวนวันที่เกิน (+วัน) ส่วนสถานะของแถวจะแสดงเป็น “เกินกำหนด” (สีแดง)',
        },
        {
          kind: 'para',
          text: 'คอลัมน์ คงค้าง และ อายุ (วัน) จัดเรียงได้ (กดหัวคอลัมน์เพื่อเรียงจากมากไปน้อย/น้อยไปมาก) ช่วยให้เห็นรายที่ค้างนานที่สุดก่อน จากแถวสามารถกดเมนู … เพื่อ เปิดดู หรือ รับชำระ ได้ทันที',
        },
        {
          kind: 'image',
          src: '/help/ar/02-overdue-tab.png',
          caption: 'แท็บ “เกินกำหนด” — เฉพาะใบที่เลยกำหนดชำระ พร้อมป้ายแดงแสดงจำนวนวันที่เกิน',
        },
      ],
    },
    {
      id: 'forecast',
      heading: '4. คาดการณ์การรับชำระ (4 สัปดาห์)',
      blocks: [
        {
          kind: 'para',
          text: 'แผง คาดการณ์การรับชำระ (4 สัปดาห์) ประมาณการเงินที่จะรับเข้าจากลูกหนี้คงค้างเป็นรายสัปดาห์ (W1–W4) แสดงเป็นกราฟแท่ง หน่วยเป็นบาท ใช้วางแผนกระแสเงินสดรับล่วงหน้า',
        },
        {
          kind: 'para',
          text: 'แผงนี้ย่อ/ขยายได้ด้วยปุ่ม แสดง/ซ่อน ระบบจะดึงข้อมูลคาดการณ์ก็ต่อเมื่อกดเปิดแผงเท่านั้น เพื่อให้หน้าหลักโหลดเร็ว',
        },
        {
          kind: 'image',
          src: '/help/ar/03-collection-forecast.png',
          caption: 'แผงคาดการณ์การรับชำระ 4 สัปดาห์ — กราฟแท่งประมาณการเงินรับเข้ารายสัปดาห์ W1–W4',
        },
      ],
    },
    {
      id: 'create-invoice',
      heading: '5. การสร้างใบแจ้งหนี้',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม สร้างใบแจ้งหนี้ (ปุ่มสีแดงด้านบน) เพื่อเปิดโมดัล แล้วกรอกหัวเอกสาร: ลูกค้า (จำเป็น) · สาขา/หน่วยงาน (ถ้ามี) · วันที่ออก (จำเป็น) · กำหนดชำระ (จำเป็น — ระบบเติมให้อัตโนมัติตามเงื่อนไขชำระของลูกค้า แก้ได้) · VAT (%) (ค่าเริ่มต้น 7) · คำอธิบาย · สกุลเงิน (THB/USD/EUR/CNY/JPY)',
        },
        {
          kind: 'para',
          text: 'ในตารางรายการรายได้ เพิ่ม/ลบบรรทัดได้: คำอธิบาย · จำนวน · ราคา/หน่วย · บัญชีรายได้ (ค่าเริ่มต้น 40110001) · ยอดเงิน (ระบบคำนวณ = จำนวน × ราคา/หน่วย) ด้านล่างสรุป ยอดก่อน VAT · VAT · รวมทั้งสิ้น ให้แบบเรียลไทม์ กรณีสกุลต่างประเทศต้องกรอกยอดเงินสกุลต่างประเทศและอัตราแลกเปลี่ยน',
        },
        {
          kind: 'callout',
          variant: 'warn',
          text: 'วงเงินเครดิต: ถ้าลูกค้ามีการกำหนดวงเงิน ระบบจะแสดงแถบวงเงินเครดิต (ใช้ไป/วงเงิน/คงเหลือ) หากเกินวงเงินจะบล็อกการสร้าง ต้องติ๊ก override (สำหรับผู้มีสิทธิ์) จึงจะดำเนินการต่อได้',
        },
        {
          kind: 'steps',
          items: [
            { title: 'เลือกลูกค้า', text: 'ระบบจะเติมกำหนดชำระตามเงื่อนไขของลูกค้า' },
            { title: 'กรอกวันที่ออก / VAT / สกุลเงิน', text: 'ปรับกำหนดชำระเองได้หากต้องการ' },
            { title: 'เพิ่มรายการรายได้', text: 'ใส่คำอธิบาย จำนวน ราคา/หน่วย และบัญชีรายได้' },
            { title: 'ตรวจยอดรวม', text: 'ดู ยอดก่อน VAT / VAT / รวมทั้งสิ้น และแถบวงเงินเครดิต' },
            {
              title: 'กด สร้างใบแจ้งหนี้',
              text: 'ระบบลงใบสำคัญ Dr ลูกหนี้ / Cr รายได้ + VAT ให้อัตโนมัติ',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/ar/04-create-invoice.png',
          caption:
            'โมดัลสร้างใบแจ้งหนี้ — เลือกลูกค้า/วันที่/VAT/รายการรายได้ พร้อมยอดก่อน-หลัง VAT และแถบวงเงินเครดิต',
        },
      ],
    },
    {
      id: 'record-receipt',
      heading: '6. การบันทึกการรับชำระ',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม รับชำระ ด้านบน หรือเลือกเมนู … ▸ รับชำระ ที่แถวใบแจ้งหนี้ หรือเลือกหลายใบของลูกค้าเดียวกันแล้วกด รับชำระที่เลือก เพื่อเปิดโมดัลบันทึกรับชำระ',
        },
        {
          kind: 'para',
          text: 'กรอกหัวเอกสาร: วันที่รับชำระ (ค่าเริ่มต้นวันนี้) · บัญชีเงินสด/ธนาคาร (ค่าเริ่มต้น: บัญชีเงินสด/ธนาคารหลักของบริษัท) · คำอธิบาย จากนั้นในตารางจัดสรร ติ๊กเลือกใบแจ้งหนี้ที่จะรับชำระ และกรอกยอดที่จัดสรรต่อใบ (ห้ามเกินยอดคงค้างของใบนั้น)',
        },
        {
          kind: 'para',
          text: 'หากมีภาษีหัก ณ ที่จ่าย ให้เลือกประเภทเงินได้ (ค่าบริการ/ค่าขนส่ง/ค่าเช่า/ค่าโฆษณา/ค่าวิชาชีพ) ระบบจะคำนวณยอดภาษีหัก ณ ที่จ่ายจากฐานก่อน VAT ให้ (แก้ได้) พร้อมช่องเลขที่/วันที่หนังสือรับรอง (50 ทวิ) ด้านล่างมีแถบสรุป: จัดสรรรวม (จำนวนใบที่ติ๊ก) · ยอดรวม · หัก ณ ที่จ่าย · เงินสุทธิ (บาท)',
        },
        {
          kind: 'image',
          src: '/help/ar/05-record-receipt.png',
          caption:
            'โมดัลบันทึกการรับชำระ — เลือกลูกค้า/บัญชีเงินสด, จัดสรรต่อใบแจ้งหนี้, หัก ณ ที่จ่าย และเงินสุทธิ',
        },
      ],
    },
    {
      id: 'import-export',
      heading: '7. การนำเข้าและส่งออกใบแจ้งหนี้',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม นำเข้า เพื่อสร้างใบแจ้งหนี้ทีละหลายใบจากไฟล์ Excel โดยหนึ่งแถว = หนึ่งใบแจ้งหนี้ที่มีรายการรายได้รายการเดียว (ใบที่มีหลายรายการ ให้ใช้ปุ่ม “สร้างใบแจ้งหนี้” แทน) ดาวน์โหลดเทมเพลตจากในโมดัลได้',
        },
        {
          kind: 'para',
          text: 'คอลัมน์ที่ต้องกรอก (มี * = จำเป็น): invoiceDate* · dueDate* · customerCode* · branchCode · departmentCode · description · revenueCode (ค่าเริ่มต้น 40110001) · amount* (ยอดก่อน VAT) · vatRate (ค่าเริ่มต้น 7) · currencyCode (ค่าเริ่มต้น THB) ระบบจะตรวจความถูกต้องทีละแถว (เช่น รหัสลูกค้า/สาขา/หน่วยงานต้องมีอยู่จริง) และแสดงผลสร้าง/อัปเดต/ล้มเหลว พร้อมรายการข้อผิดพลาด',
        },
        {
          kind: 'para',
          text: 'ปุ่ม ส่งออก ใช้บันทึกรายการใบแจ้งหนี้ (ทั้งหมดหรือชุดที่กรองไว้) เป็นไฟล์ .xlsx และเมื่อเลือกใบหลายรายการในตาราง จะมีปุ่ม ส่งออกที่เลือก ให้ส่งออกเฉพาะที่ติ๊กไว้',
        },
        {
          kind: 'image',
          src: '/help/ar/06-import.png',
          caption: 'โมดัลนำเข้าใบแจ้งหนี้ — ตารางสเปกคอลัมน์ พร้อมปุ่มดาวน์โหลดเทมเพลต',
        },
      ],
    },
    {
      id: 'related-tools',
      heading: '8. เครื่องมือลูกหนี้อื่น ๆ',
      blocks: [
        {
          kind: 'para',
          text: 'ปุ่มบนแถบเครื่องมือยังลิงก์ไปยังหน้าจัดการเฉพาะด้านของลูกหนี้:',
        },
        {
          kind: 'bullets',
          items: [
            { lead: 'รายงานอายุหนี้ — ', text: 'แยกยอดคงค้างตามช่วงอายุหนี้ของลูกค้าแต่ละราย' },
            {
              lead: 'DSO — ',
              text: 'แดชบอร์ดระยะเวลาเก็บหนี้เฉลี่ย (Days Sales Outstanding) + รายที่ชำระช้า',
            },
            { lead: 'ใบรับชำระ — ', text: 'ประวัติใบรับชำระทั้งหมดพร้อมการจัดสรร' },
            { lead: 'ใบลดหนี้ — ', text: 'ออกและอนุมัติใบลดหนี้ (Credit Note) ผ่านระบบอนุมัติ' },
            { lead: 'ใบยกเลิก — ', text: 'ขอยกเลิกใบแจ้งหนี้ (ผ่านระบบอนุมัติ ก่อนกลับรายการ JV)' },
            { lead: 'ใบวางบิล — ', text: 'รวมหลายใบแจ้งหนี้เป็นใบวางบิลเพื่อเรียกเก็บ' },
            { lead: 'เช็ครับ (PDC) — ', text: 'ทะเบียนเช็คลงวันที่ล่วงหน้า (รับ/นำฝาก/ผ่าน/เด้ง)' },
            {
              lead: 'รับล่วงหน้า — ',
              text: 'เงินรับล่วงหน้า/มัดจำจากลูกค้า และการนำไปตัดกับใบแจ้งหนี้',
            },
          ],
        },
      ],
    },
    {
      id: 'statuses',
      heading: '9. สรุปสถานะของใบแจ้งหนี้',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['รอชำระ', 'ลงบัญชีแล้ว ยังไม่ครบกำหนด/ยังรอรับชำระ (ป้ายสีน้ำเงิน)'],
            ['เกินกำหนด', 'ลงบัญชีแล้วและเลยวันครบกำหนดชำระ (ป้ายสีแดง — ต้องติดตาม)'],
            ['ชำระแล้ว', 'รับชำระครบเต็มจำนวนแล้ว (ป้ายสีเขียว)'],
            ['ยกเลิก', 'ใบแจ้งหนี้ถูกยกเลิกและกลับรายการ JV แล้ว (ป้ายสีเทา)'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '10. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'สร้างใบแจ้งหนี้ไม่ได้ / ปุ่มกดไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ปุ่มจะกดได้ต่อเมื่อเลือกลูกค้า มีวันที่ออก/กำหนดชำระ มีรายการรายได้อย่างน้อย 1 บรรทัดที่ยอดมากกว่า 0 และไม่ติดบล็อกวงเงินเครดิต',
            },
          ],
        },
        { kind: 'para', text: 'รับชำระแล้วยอดไม่ตรง / กรอกยอดจัดสรรไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ยอดที่จัดสรรต่อใบต้องไม่เกินยอดคงค้างของใบนั้น มิฉะนั้นแถบสรุปจะขึ้นสถานะผิดพลาดและบันทึกไม่ได้',
            },
          ],
        },
        { kind: 'para', text: 'จะยกเลิกใบแจ้งหนี้อย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ยกเลิกได้เฉพาะใบสถานะ “รอชำระ” ที่ยังไม่มีการรับชำระและไม่มีใบลดหนี้ ผ่านเมนู … ▸ ยกเลิก แล้วส่งเข้าระบบอนุมัติ',
            },
          ],
        },
        { kind: 'para', text: 'นำเข้าใบแจ้งหนี้แล้วบางแถวล้มเหลว?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ตรวจว่ารหัสลูกค้า/สาขา/หน่วยงานมีอยู่จริง และคอลัมน์ที่จำเป็น (invoiceDate, dueDate, customerCode, amount) ครบถ้วน ระบบจะระบุข้อผิดพลาดเป็นรายแถว',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '11. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ลูกหนี้การค้า (AR) — ',
              text: 'ยอดที่ลูกค้าค้างชำระจากการขายสินค้า/บริการเชื่อ',
            },
            { lead: 'คงค้าง — ', text: 'ยอดที่ยังไม่ได้รับชำระ = มูลค่ารวม − รับชำระ − ใบลดหนี้' },
            { lead: 'เกินกำหนด — ', text: 'ใบที่เลยวันครบกำหนดชำระแล้ว' },
            {
              lead: 'DSO — ',
              text: 'ระยะเวลาเก็บหนี้เฉลี่ย (Days Sales Outstanding) ยิ่งสูงยิ่งเก็บหนี้ช้า',
            },
            {
              lead: 'ภาษีหัก ณ ที่จ่าย (WHT) — ',
              text: 'ภาษีที่ลูกค้าหักไว้ตอนจ่ายเงิน พร้อมหนังสือรับรอง 50 ทวิ',
            },
            { lead: 'VAT — ', text: 'ภาษีมูลค่าเพิ่ม (ค่าเริ่มต้น 7%) คิดบนยอดก่อน VAT' },
            {
              lead: 'วงเงินเครดิต — ',
              text: 'วงเงินสูงสุดที่ให้ลูกค้าค้างชำระได้ เกินต้อง override/อนุมัติ',
            },
            { lead: 'ใบลดหนี้ — ', text: 'เอกสารลดยอดลูกหนี้ (Credit Note) เช่น คืนของ/ลดราคา' },
            {
              lead: 'เช็ครับ (PDC) — ',
              text: 'เช็คลงวันที่ล่วงหน้าที่รับจากลูกค้า รอถึงกำหนดจึงนำเข้า',
            },
          ],
        },
      ],
    },
  ],
};
